import { useState, useEffect, useRef } from "react";
import { DiscordSDK } from "@discord/embedded-app-sdk";

export interface DiscordUser {
  id: string;
  username: string;
  avatar: string | null;
  global_name: string | null;
}

interface DiscordSdkState {
  ready: boolean;
  user: DiscordUser | null;
  sessionToken: string | null;
  error: Error | null;
  openExternalLink: (url: string) => Promise<void>;
}

let discordSdk: DiscordSDK | null = null;

export function useDiscordSdk(clientId: string): DiscordSdkState {
  const [state, setState] = useState<DiscordSdkState>({
    ready: false,
    user: null,
    sessionToken: null,
    error: null,
    openExternalLink: async () => {},
  });
  const initCalled = useRef(false);

  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    async function init() {
      try {
        discordSdk = new DiscordSDK(clientId);
        await discordSdk.ready();

        // Authorize — prompts the user in Discord to grant access
        const { code } = await discordSdk.commands.authorize({
          client_id: clientId,
          response_type: "code",
          state: "",
          prompt: "none",
          scope: ["identify"],
        });

        // Exchange the code for an access token via our backend
        console.log("[Discord] Got code, exchanging for token...");
        const res = await fetch("/api/auth/discord", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });

        const data = await res.json();
        console.log("[Discord] Server response:", res.status, data);

        if (!res.ok) throw new Error(data.error || `Auth failed (${res.status}): ${JSON.stringify(data.details)}`);

        // Authenticate with the Discord SDK so it knows who we are
        const auth = await discordSdk.commands.authenticate({
          access_token: data.access_token,
        });

        // Check if we're returning from a Spotify OAuth redirect
        const params = new URLSearchParams(window.location.search);
        const returnedSession = params.get("session");
        const sessionToken = returnedSession || data.sessionToken;

        // Clean up URL params if present
        if (returnedSession) {
          window.history.replaceState({}, "", "/");
        }

        const sdk = discordSdk!;
        setState({
          ready: true,
          user: auth.user as unknown as DiscordUser,
          sessionToken,
          error: null,
          openExternalLink: async (url: string) => {
            await sdk.commands.openExternalLink({ url });
          },
        });
      } catch (err) {
        console.error("Discord SDK init failed:", err);
        setState((prev) => ({ ...prev, error: err as Error }));
      }
    }

    init();
  }, []);

  return state;
}
